import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import { FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { Panel } from 'shared/components'
import { useUserRoles } from 'shared/hooks'
import { cn } from 'utils/style'

type AccessControlProps = {
	form: UseFormReturn<FieldValues>
}

export const AccessControl = ({ form }: AccessControlProps) => {
	const { t } = useTranslation()
	const { isSuperuser, isAdmin } = useUserRoles()
	const { register } = form

	const accessOptions = [
		{
			name: 'is_active',
			label: t('Active'),
			hint: t('Can sign in'),
			disabled: !isAdmin && !isSuperuser
		},
		{ name: 'is_staff', label: t('Staff'), hint: t('Store employee'), disabled: !isAdmin },
		{ name: 'is_admin', label: t('Admin'), hint: t('Manages the store'), disabled: !isAdmin },
		{ name: 'is_superuser', label: t('Superuser'), hint: t('Full access'), disabled: !isSuperuser }
	]

	return (
		<Panel title={t('Access control')} icon={<ShieldCheckIcon />}>
			<p className="-mt-2 mb-3 text-[12px] text-muted-foreground">
				{t('What this user is allowed to do.')}
			</p>
			<div className="grid grid-cols-2 gap-2">
				{accessOptions.map((option) => (
					<label
						key={option.name}
						className={cn(
							'flex items-start gap-2.5 rounded-md border border-border p-2.5 transition-colors [&:has(:checked)]:border-primary [&:has(:checked)]:bg-primary-soft',
							option.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted/50'
						)}>
						<input
							type="checkbox"
							className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
							disabled={option.disabled}
							{...register(option.name)}
						/>
						<span className="min-w-0 leading-tight">
							<span className="block text-[13px] font-semibold text-foreground">
								{option.label}
							</span>
							<span className="block text-[11px] text-muted-foreground">{option.hint}</span>
						</span>
					</label>
				))}
			</div>
		</Panel>
	)
}
